import React, { FunctionComponent } from 'react';
import { Formik, FormikHelpers, Field, FormikProps } from 'formik';
import { object, string } from 'yup';

// utils
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

// constants
import Rules from 'restrictions/roles/constants/rules';
import { API_ROOT } from 'constants/index';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// types
import { ButtonConfig } from 'pages/MerchantPages/Settings/Integrations/OvpIntegrations/components/BrightcoveForm';

// Components
import { Grid, Button } from '@inplayer-org/inplayer-ui';
import Restricted from 'restrictions/roles/components/Restricted';
import TextArea from 'components/Fields/TextArea';
import Input from 'components/Fields/Input';
import FieldError from 'components/Fields/FieldError';
import ClipBoardButton from 'components/ClipBoardButton';
import Alert from 'components/Alert';

const { Container, Cell } = Grid;

type FormValues = {
  secretKey: string;
  p8_certificate: string;
  bundle_id: string;
  key_id: string;
  issuer: string;
};

interface OwnProps {
  onSubmit: (data: FormValues) => any;
  appleInAppKeys: FormValues;
  merchantUuid: string;
}

const validationSchema = object().shape({
  p8_certificate: string()
    .required('*Required')
    .test('p8_certificate', '*Incorrect format', (value = '') =>
      /^-----BEGIN PRIVATE KEY-----[\s\S]*-----END PRIVATE KEY-----$/.test(value)
    ),
  bundle_id: string().required('*Required'),
  key_id: string().required('*Required'),
  issuer: string().required('*Required'),
});

type Props = OwnProps;

const AppleInAppForm: FunctionComponent<Props> = ({ onSubmit, appleInAppKeys: initialValues, merchantUuid }) => {
  const { hasRule } = useRestrictionRules();

  const onFormSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    await onSubmit(values);
    setSubmitting(false);
  };

  const saveButtonProps: ButtonConfig = {
    label: 'Save',
  };

  const updateButtonProps: ButtonConfig = {
    label: 'Update',
    modifier: 'buttonPrimary',
  };

  const buttonProps = !isEmpty(initialValues) ? updateButtonProps : saveButtonProps;
  const buttonModifiers = buttonProps.modifier ? [buttonProps.modifier] : [];
  const appleNotificationUrl = `${API_ROOT}v2/hooks/apple-in-app/${merchantUuid}`;

  const isReadOnly = hasRule(Rules.READONLY_IN_APP_INTEGRATIONS);

  return (
    <Formik
      onSubmit={onFormSubmit}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, values }: FormikProps<FormValues>) => (
        <form onSubmit={handleSubmit}>
          <Container columns={3} rowGap="1rem">
            {values.secretKey && isEmpty(omit(initialValues, ['secretKey', 'descriptor'])) ? (
              <>
                <Cell width={3}>
                  <Field
                    tag="text_app_store_app_specific_shared_secret"
                    name="secretKey"
                    type="text"
                    component={TextArea}
                    label="App-Specific Shared Secret"
                    placeholder="Enter App-Specific Shared Secret"
                    disabled={isReadOnly}
                  />
                  <FieldError name="secretKey" />
                </Cell>
                <Cell width={3}>
                  <Alert>
                    <div>
                      <p>
                        The Apple App Store Server Notifications V1 endpoint and notifications are deprecated. Implement
                        the V2 endpoint on your server if you want to migrate your integration.{' '}
                      </p>
                      <p>Your existing integration will remain functional until the update is made. </p>
                    </div>
                    <p>
                      Before updating your integration, contact your Account manager to discuss the migration steps.
                    </p>
                  </Alert>
                </Cell>
              </>
            ) : null}
            <Cell width={3}>
              <Field
                tag="text_app_store_bundle_id"
                name="bundle_id"
                type="text"
                component={Input}
                label="Bundle ID"
                placeholder="Enter Bundle ID"
                helperText="Application ID in the Apple system."
                disabled={isReadOnly}
              />
              <FieldError name="bundle_id" />
            </Cell>
            <Cell width={3}>
              <Field
                tag="text_app_store_p8_certificate"
                name="p8_certificate"
                type="text"
                component={TextArea}
                label="P8 Certificate"
                placeholder="Enter P8 Certificate"
                helperText="Value of the private key."
                disabled={isReadOnly}
              />
              <FieldError name="p8_certificate" />
            </Cell>
            <Cell width={3}>
              <Field
                tag="text_app_store_key_id"
                name="key_id"
                type="text"
                component={Input}
                label="Key ID"
                placeholder="Enter Key ID"
                disabled={isReadOnly}
              />
              <FieldError name="key_id" />
            </Cell>
            <Cell width={3}>
              <Field
                tag="text_app_store_issuer"
                name="issuer"
                type="text"
                component={Input}
                label="Issuer"
                placeholder="Enter Issuer"
                helperText="UUID4 string"
                disabled={isReadOnly}
              />
              <FieldError name="issuer" />
            </Cell>
            <Cell width={3}>
              <Container columns="auto 1fr" gap="0.75rem">
                <Cell width={2}>
                  <p>Connect InPlayer with Apple platforms by adding a notification URL to your Apple dashboard.</p>
                </Cell>
                <Cell>
                  <ClipBoardButton
                    tag="app_store_copy_url"
                    clipboardText={appleNotificationUrl}
                    tooltipText="Copied!"
                    buttonModifiers={['buttonPrimary']}
                    fullWidth
                    placement="top"
                  >
                    Copy URL
                  </ClipBoardButton>
                </Cell>
              </Container>
            </Cell>
            <Restricted isRestricted={isReadOnly}>
              <Cell width={1} left={3}>
                <Button
                  tag={`button_app_store_${buttonProps.label.toLowerCase()}`}
                  type="submit"
                  buttonModifiers={buttonModifiers}
                  fullWidth
                  disabled={isSubmitting}
                >
                  {buttonProps.label}
                </Button>
              </Cell>
            </Restricted>
          </Container>
        </form>
      )}
    </Formik>
  );
};

export default AppleInAppForm;
